import { type ReactNode, type MouseEvent, useState } from 'react';
import { type LinkProps } from 'react-router';
import { ButtonSpinner, IconLeft, IconRight, Label, StyledButton, StyledLink } from './style';

export type ButtonProps = {
  children?: ReactNode;
  variant?:
    'primary' | 'secondary' | 'tertiary' | 'text-primary' | 'text-secondary' | 'text-primary-red';
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  icon?: ReactNode;
  iconPlacement?: 'before' | 'after';
  fullWidth?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  id?: string;
  testId?: string;
  preventDoubleClick?: boolean;
  className?: string;
  underlined?: boolean;
};

type ButtonLinkProps = ButtonProps & {
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
} & LinkProps;

type ButtonComponentProps = (ButtonProps | ButtonLinkProps) & {
  ref?: React.Ref<HTMLButtonElement | HTMLAnchorElement>;
};

function ButtonComponent(props: ButtonComponentProps) {
  const { ref } = props;
  const [disabledFromClick, setDisabledFromClick] = useState(false);

  const {
    children,
    variant,
    icon,
    iconPlacement = 'before',
    fullWidth,
    disabled,
    isLoading,
    id,
    preventDoubleClick = true,
    testId,
    onClick,
    underlined,
  } = props;

  const onButtonClick = (e: MouseEvent<HTMLButtonElement> & MouseEvent<HTMLAnchorElement>) => {
    if (preventDoubleClick) {
      setDisabledFromClick(true);
      setTimeout(() => {
        if (!disabled) {
          setDisabledFromClick(false);
        } else {
          setDisabledFromClick(true);
        }
      }, 100);
    }
    if (!isLoading && onClick) {
      onClick(e);
    }
  };

  const content = (
    <>
      {!isLoading && icon && iconPlacement === 'before' && <IconLeft>{icon}</IconLeft>}
      {children && (
        <Label variant={variant} icon={!!icon} iconPlacement={iconPlacement} isLoading={isLoading}>
          {children}
        </Label>
      )}
      {isLoading && <ButtonSpinner color="currentColor" />}
      {!isLoading && icon && iconPlacement === 'after' && <IconRight>{icon}</IconRight>}
    </>
  );

  if (isLink(props)) {
    const buttonLinkProps = props as ButtonLinkProps;
    const rest = { ...buttonLinkProps };

    return (
      <StyledLink
        data-testid={testId ?? 'button'}
        {...rest} //To prevent the other props being overwritten we add this first
        id={id}
        ref={ref as React.Ref<HTMLAnchorElement>}
        onClick={onButtonClick}
        variant={variant ?? 'primary'}
        fullWidth={fullWidth}
        disabled={disabledFromClick || disabled}
        isLoading={isLoading}
        hasIcon={!!icon}
      >
        {content}
      </StyledLink>
    );
  } else {
    const rest = { ...props };

    return (
      <StyledButton
        data-testid={testId ?? 'button'}
        {...rest} //To prevent the other props being overwritten we add this first
        id={id}
        ref={ref as React.Ref<HTMLButtonElement>}
        onClick={onButtonClick}
        variant={variant ?? 'primary'}
        fullWidth={fullWidth}
        disabled={disabledFromClick || disabled || isLoading}
        isLoading={isLoading}
        hasIcon={!!icon}
        underlined={underlined}
      >
        {content}
      </StyledButton>
    );
  }
}

function isLink(props: ButtonProps | (ButtonProps & LinkProps)): props is ButtonProps & LinkProps {
  return 'to' in props;
}

export const Button = ButtonComponent;
